import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import ErrorSummary from '../../components/ErrorSummary/ErrorSummary';
import ErrorMessage from '../../components/form/ErrorMessage';
import Input from '../../components/form/TextInput';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import { P } from '../../components/ui';
import config from '../../config';
import content from '../../content';
import { useFormConfig } from '../../hooks/useFormConfig';
import { triggerRegisterPasswordReset } from '../../service/form';
import { ApiError } from '../../utils/api-error';
import { validateField } from '../../utils/form-utils';

const label = 'Email address';
const resetSentTitle = 'Check your email';

export default function ResetPasswordEmailScreen() {
  const [searchParams] = useSearchParams();
  const prefilledEmail = (searchParams.get('email') || '').trim().toLowerCase();
  const {
    formState: { errors },
    handleSubmit,
    control,
    setError,
  } = useForm<{ email: string }>({
    ...config.form,
    defaultValues: { email: prefilledEmail },
  });
  const [disabled, setDisabled] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const { tenant } = useFormConfig();
  const signInPath = tenant ? `/${tenant}/${config.urls.signin}` : `/${config.urls.signin}`;
  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => triggerRegisterPasswordReset(email, tenant),
  });

  if (submittedEmail) {
    return (
      <PageTemplate title={resetSentTitle} backLink={signInPath}>
        <P>
          If an account exists for <b>{submittedEmail}</b>, we have sent sign in details to that
          email address.
        </P>
        <P>You may need to check your junk email or spam folder.</P>
        <Link
          to={signInPath}
          className="govuk-button"
          data-module="govuk-button"
          data-prevent-double-click="true"
        >
          {content.buttonGoToSignIn}
        </Link>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title="Reset password" backLink={signInPath}>
      <ErrorSummary errors={errors} idPrefix="field-" />

      <P>
        Enter the email address you are registered with. To confirm it is your email address we will
        send you a code.
      </P>
      <form
        onSubmit={handleSubmit(async (data) => {
          try {
            setDisabled(true);
            const normalizedEmail = data.email.trim().toLowerCase();
            const result = await resetPasswordMutation.mutateAsync(normalizedEmail);
            setSubmittedEmail(result.email || normalizedEmail);
          } catch (err) {
            setDisabled(false);
            const message =
              err instanceof ApiError
                ? err.detail || err.message || content.errorApi
                : (err as Error).message || content.errorApi;
            setError('email', { message });
            return;
          }
          setDisabled(false);
        })}
        noValidate
      >
        <div className={`govuk-form-group ${errors?.email && 'govuk-form-group--error'}`}>
          <label htmlFor="field-email" className="govuk-label">
            {label}
          </label>
          <Controller
            name="email"
            control={control}
            rules={{
              validate: (value: string) =>
                validateField(label, { required: true, email: true }, value),
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                {error && <ErrorMessage error={error} />}
                <Input
                  {...field}
                  id="field-email"
                  type="email"
                  width="three-quarters"
                  autoComplete="off"
                  disabled={disabled}
                />
              </>
            )}
          />
        </div>
        <button
          type="submit"
          className="govuk-button"
          data-module="govuk-button"
          data-prevent-double-click="true"
          disabled={disabled}
        >
          {content.buttonContinue}
        </button>
      </form>
    </PageTemplate>
  );
}
